import { msg } from '@lingui/macro'
import { translateStatsConfigLabel } from './stats-config-label-messages'
import {
  resolveOptionDisplayLabel,
  resolveOptionMapGeoName,
} from '../../../utils/config-localization'

/** Campi calcolati lato API non ancora in structure.json. */
export const VIRTUAL_STATS_CHART_FIELDS = Object.freeze([
  { target: 'people', fieldName: 'Age group' },
])

/** Nomi equivalenti per lo stesso campo in config/API (es. rinominato State → Region). */
const STATS_FIELD_NAME_ALIASES = Object.freeze({
  State: ['State', 'Region'],
  Region: ['Region', 'State'],
})

/**
 * @param {string} fieldName
 * @returns {string[]}
 */
function getStatsFieldNameCandidates(fieldName) {
  const key = String(fieldName ?? '').trim()
  if (!key) return []
  return STATS_FIELD_NAME_ALIASES[key] ?? [key]
}

const MONTH_ORDER = ['01', '02', '03', '04', '05', '06', '07', '08', '09', '10', '11', '12']

const MONTH_LABEL_BY_CODE = Object.freeze({
  '01': msg`January`,
  '02': msg`February`,
  '03': msg`March`,
  '04': msg`April`,
  '05': msg`May`,
  '06': msg`June`,
  '07': msg`July`,
  '08': msg`August`,
  '09': msg`September`,
  '10': msg`October`,
  '11': msg`November`,
  '12': msg`December`,
})

const DAY_OF_WEEK_LABEL_BY_KEY = Object.freeze({
  monday: msg`Monday`,
  tuesday: msg`Tuesday`,
  wednesday: msg`Wednesday`,
  thursday: msg`Thursday`,
  friday: msg`Friday`,
  saturday: msg`Saturday`,
  sunday: msg`Sunday`,
})

const DAY_OF_WEEK_ORDER = [
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
  'Sunday',
]

const AGE_GROUP_ORDER = [
  '0-17',
  '18-24',
  '25-34',
  '35-44',
  '45-54',
  '55-64',
  '65+',
  'Unknown',
]

/**
 * Estrae righe grafico da risposta Stats (array legacy o wrapper StatsResponse).
 * @param {unknown} responseData
 * @returns {Array<Record<string, unknown>>}
 */
/** @deprecated Prefer unwrapStatsChartRows for StatsResponse.chartData */
export function extractChartRowsFromStatsResponse(responseData) {
  const unwrapped = unwrapStatsChartRows(responseData)
  if (unwrapped.length > 0) return unwrapped
  if (responseData && typeof responseData === 'object') {
    const obj = /** @type {Record<string, unknown>} */ (responseData)
    if (Array.isArray(obj.chartResultValues)) return obj.chartResultValues
    if (Array.isArray(obj.ChartResultValues)) return obj.ChartResultValues
  }
  return []
}

/**
 * Righe grafico da risposta Stats (array legacy o StatsResponse.chartData).
 * @param {unknown} responseData
 * @returns {Array<Record<string, unknown>>}
 */
export function unwrapStatsChartRows(responseData) {
  if (Array.isArray(responseData)) return responseData
  if (responseData && typeof responseData === 'object') {
    const obj = /** @type {Record<string, unknown>} */ (responseData)
    const chartData = obj.chartData ?? obj.ChartData
    if (Array.isArray(chartData)) return chartData
  }
  return []
}

/** Alias consigliato dalla checklist backend. */
export const extractStatsChartRows = extractChartRowsFromStatsResponse

/**
 * @param {unknown} responseData
 * @returns {Record<string, unknown> | null}
 */
export function extractSeveritySummaryFromStatsResponse(responseData) {
  if (!responseData || typeof responseData !== 'object' || Array.isArray(responseData)) {
    return null
  }
  const obj = /** @type {Record<string, unknown>} */ (responseData)
  const summary = obj.severitySummary ?? obj.SeveritySummary
  return summary && typeof summary === 'object' ? /** @type {Record<string, unknown>} */ (summary) : null
}

/**
 * @param {Record<string, unknown> | null | undefined} raw
 */
export function normalizeSeveritySummary(raw) {
  if (!raw || typeof raw !== 'object') {
    return null
  }
  return {
    total: Number(raw.total ?? 0),
    fatal: Number(raw.fatalSeverity ?? raw.fatal ?? 0),
    seriousInjury: Number(raw.seriousInjurySeverity ?? raw.seriousInjury ?? 0),
    slightInjury: Number(raw.slightInjurySeverity ?? raw.slightInjury ?? 0),
    noInjury: Number(raw.noInjurySeverity ?? raw.noInjury ?? 0),
    unknown: Number(raw.unknownSeverity ?? raw.unknown ?? 0),
    totalPeople: Number(raw.totalPeople ?? 0),
  }
}

/**
 * @param {unknown} responseData
 * @returns {Array<Record<string, unknown>>}
 */
export function extractYearlySummaryFromStatsResponse(responseData) {
  if (Array.isArray(responseData)) {
    return responseData
  }
  if (!responseData || typeof responseData !== 'object') {
    return []
  }
  const obj = /** @type {Record<string, unknown>} */ (responseData)
  const rows = obj.yearlySummary ?? obj.YearlySummary
  return Array.isArray(rows) ? rows : []
}

/**
 * Costruisce payload POST /api/Stats per un grafico dashboard.
 * @param {object} chart
 * @param {Array<Record<string, unknown>>} [filters]
 */
export function buildStatsChartRequestPayload(chart, filters = [], fields) {
  const groupBy =
    chart.payload?.groupBy ??
    chart.groupByFormat ??
    chart.payload?.groupByFormat ??
    chart.payload?.chartField?.groupBy

  const payload = {
    ...chart.payload,
    filters: filters.length > 0 ? filters : [],
  }

  if (fields) {
    payload.fields = fields
  }

  if (groupBy) {
    payload.groupBy = groupBy
  }

  return payload
}

/**
 * Payload generico Stats (CustomChart, yearly-summary, ecc.).
 * @param {object} params
 */
export function buildStatsRequestPayload({
  chartField,
  fields,
  filters = [],
  groupBy,
  countMode,
  responseFormat,
}) {
  const payload = {
    chartField,
    fields,
    filters: filters.length > 0 ? filters : [],
  }
  if (groupBy) payload.groupBy = groupBy
  if (countMode) payload.countMode = countMode
  if (responseFormat) payload.responseFormat = responseFormat
  return payload
}

/**
 * @param {Record<string, unknown>} row
 */
export function normalizeYearlySummaryRow(row) {
  return {
    year: Number(row.year ?? row.Year ?? 0),
    total: Number(row.total ?? row.Total ?? 0),
    fatal: Number(row.fatal ?? row.Fatal ?? 0),
    seriousInjury: Number(row.seriousInjury ?? row.SeriousInjury ?? 0),
    slightInjury: Number(row.slightInjury ?? row.SlightInjury ?? 0),
    noInjury: Number(row.noInjury ?? row.NoInjury ?? 0),
    unknown: Number(row.unknown ?? row.Unknown ?? 0),
  }
}

/**
 * @param {string | number | undefined} label
 * @returns {string | null}
 */
export function normalizeMonthCode(label) {
  const trimmed = String(label ?? '').trim()
  if (/^\d{1,2}$/.test(trimmed)) {
    const monthNumber = parseInt(trimmed, 10)
    if (monthNumber >= 1 && monthNumber <= 12) {
      return String(monthNumber).padStart(2, '0')
    }
  }
  return MONTH_ORDER.includes(trimmed) ? trimmed : null
}

/**
 * @param {string | number | undefined} label
 * @param {(descriptor: import('@lingui/macro').MessageDescriptor) => string} translate
 */
export function localizeMonthLabel(label, translate) {
  const code = normalizeMonthCode(label)
  if (code && MONTH_LABEL_BY_CODE[code]) {
    return translate(MONTH_LABEL_BY_CODE[code])
  }
  return String(label ?? '')
}

/**
 * @param {string | number | undefined} label
 * @param {(descriptor: import('@lingui/macro').MessageDescriptor) => string} translate
 */
const DAY_OF_WEEK_NUMERIC_TO_KEY = Object.freeze({
  '0': 'sunday',
  '1': 'monday',
  '2': 'tuesday',
  '3': 'wednesday',
  '4': 'thursday',
  '5': 'friday',
  '6': 'saturday',
  '7': 'sunday',
})

export function localizeDayOfWeekLabel(label, translate) {
  const raw = String(label ?? '').trim()
  const key = DAY_OF_WEEK_NUMERIC_TO_KEY[raw] ?? raw.toLowerCase()
  if (DAY_OF_WEEK_LABEL_BY_KEY[key]) {
    return translate(DAY_OF_WEEK_LABEL_BY_KEY[key])
  }
  return raw
}

/**
 * @param {string | number | undefined} rawLabel
 * @param {string | undefined} groupBy
 * @param {(descriptor: import('@lingui/macro').MessageDescriptor) => string} translate
 */
function translateChartPointLabel(rawLabel, groupBy, translate) {
  if (groupBy === 'MM') {
    return localizeMonthLabel(rawLabel, translate)
  }
  if (groupBy === 'dayOfWeek') {
    return localizeDayOfWeekLabel(rawLabel, translate)
  }
  return translateStatsConfigLabel(rawLabel, translate)
}

/**
 * @param {Array<{ label?: string, name?: string, value: number }>} rows
 * @param {object} chart
 * @param {(descriptor: import('@lingui/macro').MessageDescriptor) => string} translate
 */
function translateChartDataRows(rows, chart, translate) {
  const groupBy =
    chart.payload?.groupBy ??
    chart.groupByFormat ??
    chart.payload?.groupByFormat

  return rows.map((row) => {
    const rawLabel = row.label ?? row.name ?? ''
    const label = translateChartPointLabel(rawLabel, groupBy, translate)
    if (chart.type === 'map') {
      return { ...row, label }
    }
    return { ...row, label }
  })
}

/**
 * Tutte le rappresentazioni testuali di un elemento config (label canonica, displayLabel, traduzioni).
 * @param {{ label?: string, displayLabel?: string, labels?: Record<string, string> }} el
 * @returns {string[]}
 */
function getConfigElementLabelVariants(el) {
  const variants = [el?.label, el?.displayLabel]
  if (el?.labels && typeof el.labels === 'object') {
    variants.push(...Object.values(el.labels))
  }
  return variants
    .filter((value) => typeof value === 'string' && value.trim() !== '')
    .map((value) => value.trim().toLowerCase())
}

/**
 * @param {{ label?: string, displayLabel?: string, labels?: Record<string, string> }} el
 * @param {string} fieldName
 */
function configElementMatchesFieldName(el, fieldName) {
  const target = String(fieldName ?? '').trim().toLowerCase()
  if (!target) return false
  return getConfigElementLabelVariants(el).includes(target)
}

/**
 * @param {object} configuration
 * @param {string} target
 * @param {string} fieldName
 * @returns {object | null}
 */
export function findStatsConfigElement(configuration, target, fieldName) {
  if (
    VIRTUAL_STATS_CHART_FIELDS.some(
      (f) => f.target === target && f.fieldName === fieldName,
    )
  ) {
    return null
  }
  const elements = configuration?.[target]?.elements
  if (!Array.isArray(elements)) return null
  for (const candidate of getStatsFieldNameCandidates(fieldName)) {
    const found = elements.find((el) => configElementMatchesFieldName(el, candidate))
    if (found) return found
  }
  return null
}

/**
 * Risolve il nome campo da inviare all'API Stats (label canonica in config).
 * @param {object} configuration
 * @param {string} target
 * @param {string} fieldName
 * @returns {string}
 */
export function resolveStatsChartFieldName(configuration, target, fieldName) {
  const elem = findStatsConfigElement(configuration, target, fieldName)
  return elem?.label ?? fieldName
}

/**
 * Campo presente in config ma marcato Sensitive (isMasked).
 * @param {object} configuration
 * @param {string} target
 * @param {string} fieldName
 */
export function isChartFieldSensitiveInStatsConfig(configuration, target, fieldName) {
  const elem = findStatsConfigElement(configuration, target, fieldName)
  return elem?.isMasked === true
}

/**
 * @param {object} configuration
 * @param {string} target
 * @param {string} fieldName
 */
export function chartFieldExistsInStatsConfig(configuration, target, fieldName) {
  if (
    VIRTUAL_STATS_CHART_FIELDS.some(
      (f) => f.target === target && f.fieldName === fieldName,
    )
  ) {
    return Array.isArray(configuration?.[target]?.elements)
  }
  const elem = findStatsConfigElement(configuration, target, fieldName)
  return elem != null && elem.isMasked !== true
}

/**
 * @param {Array<{ label?: string, name?: string, value?: number, otherFormats?: Array<{ label: string, value: string }> }>} rows
 * @param {string | undefined} groupByFormat
 * @param {string | undefined} groupBy
 */
export function aggregateStatsChartRows(rows, groupByFormat, groupBy) {
  const effectiveGroupBy = groupBy ?? groupByFormat
  if (!effectiveGroupBy) {
    return rows
  }

  const grouped = {}
  const result = []

  for (const item of rows) {
    let key = item.label ?? item.name ?? ''

    if (groupByFormat && item.otherFormats?.length) {
      const fmt = item.otherFormats.find((f) => f.label === groupByFormat)
      if (fmt) {
        key = fmt.value
      }
    }

    if (!grouped[key]) {
      grouped[key] = { label: key, value: 0 }
      result.push(grouped[key])
    }
    grouped[key].value += typeof item.value === 'number' && !Number.isNaN(item.value)
      ? item.value
      : 0
  }

  if (effectiveGroupBy === 'MM') {
    return sortByOrder(result, MONTH_ORDER)
  }
  if (effectiveGroupBy === 'dayOfWeek') {
    return sortByOrder(result, DAY_OF_WEEK_ORDER, true)
  }
  if (effectiveGroupBy === 'Age group') {
    return sortByOrder(result, AGE_GROUP_ORDER, true)
  }

  return result
}

/**
 * @param {Array<{ label: string, value: number }>} items
 * @param {string[]} order
 * @param {boolean} [caseInsensitive]
 */
function sortByOrder(items, order, caseInsensitive = false) {
  const normalize = (s) => {
    const v = String(s ?? '').trim()
    return caseInsensitive ? v.toLowerCase() : v
  }
  const indexOf = (label) => {
    const n = normalize(label)
    return order.findIndex((o) => normalize(o) === n)
  }
  return [...items].sort((a, b) => {
    const ia = indexOf(a.label)
    const ib = indexOf(b.label)
    if (ia === -1 && ib === -1) return String(a.label).localeCompare(String(b.label))
    if (ia === -1) return 1
    if (ib === -1) return -1
    return ia - ib
  })
}

/**
 * Normalizza righe Stats in dati ECharts.
 * @param {object} params
 * @param {Array<Record<string, unknown>>} params.rawRows
 * @param {object} params.formModel
 * @param {object} params.chart
 * @param {(id: import('@lingui/macro').MessageDescriptor) => string} params.translate
 */
export function normalizeStatsRowsToChartData({
  rawRows,
  formModel,
  chart,
  translate,
}) {
  const target = chart.payload.chartField.target
  const fieldName = chart.payload.chartField.fieldName
  const foundGroup = formModel[target]
  if (!foundGroup) return []

  const isVirtualField = VIRTUAL_STATS_CHART_FIELDS.some(
    (f) => f.target === target && f.fieldName === fieldName,
  )
  const foundField = findStatsConfigElement(formModel, target, fieldName)
  const foundOptions =
    !isVirtualField && foundField?.options?.length
      ? foundField.options
      : []
  let resData = rawRows

  if (foundOptions.length > 0) {
    resData = foundOptions
      .map((opt) => {
        if (chart.type === 'map' && isUnknownRegionOption(opt)) {
          return null
        }
        const match = rawRows.find((r) => statsRowMatchesOption(r, opt))
        const value = match && typeof match.value === 'number' ? match.value : 0
        const optionLabel =
          resolveOptionDisplayLabel(opt) || opt.label || opt.Label || ''
        if (chart.type === 'map') {
          const geoName = resolveOptionMapGeoName(opt)
          if (!geoName) return null
          return { label: optionLabel, name: geoName, value }
        }
        return { label: optionLabel, value }
      })
      .filter(Boolean)
  }

  const groupBy =
    chart.payload?.groupBy ??
    chart.groupByFormat ??
    chart.payload?.groupByFormat
  const aggregated = aggregateStatsChartRows(
    resData,
    chart.groupByFormat,
    groupBy,
  )
  if (groupBy) {
    return translateChartDataRows(aggregated, chart, translate)
  }

  const mappedResData = resData.map((opt) => {
    if (opt.otherFormats?.length) {
      const third = opt.otherFormats[2]
      if (third?.value != null) {
        return { ...opt, label: third.value }
      }
    }
    return opt
  })

  const result = []
  mappedResData.reduce((acc, val) => {
    const key = chart.type === 'map' ? val.name : val.label
    if (!acc[key]) {
      acc[key] =
        chart.type === 'map'
          ? { name: key, label: val.label ?? key, value: 0 }
          : { label: key, value: 0 }
      result.push(acc[key])
    }
    acc[key].value += typeof val.value === 'number' ? val.value : 0
    return acc
  }, {})

  return translateChartDataRows(result, chart, translate)
}

/**
 * @param {{ label?: string, Label?: string, value?: string | number, labels?: Record<string, string> }} opt
 */
function getOptionMatchVariants(opt) {
  const variants = new Set()
  const add = (value) => {
    const normalized = String(value ?? '').trim()
    if (normalized) variants.add(normalized)
  }

  add(opt.label)
  add(opt.Label)
  add(opt.value)
  if (opt.labels && typeof opt.labels === 'object') {
    Object.values(opt.labels).forEach(add)
  }

  return variants
}

/**
 * @param {{ label?: string, Label?: string, value?: string | number, labels?: Record<string, string> }} opt
 */
function isUnknownRegionOption(opt) {
  const value = String(opt?.value ?? '').trim()
  if (value !== '0') return false

  const labels = [opt?.label, opt?.Label, opt?.labels?.en]
    .filter((entry) => entry != null && String(entry).trim() !== '')
    .map((entry) => String(entry).trim().toLowerCase())

  return labels.includes('unknown')
}

function statsRowMatchesOption(row, opt) {
  const candidates = [row.label, row.name]
    .filter((v) => v != null && String(v).trim() !== '')
    .map((v) => String(v).trim())

  const optVariants = getOptionMatchVariants(opt)

  return candidates.some((candidate) =>
    [...optVariants].some(
      (variant) =>
        candidate === variant ||
        candidate.toLowerCase() === variant.toLowerCase(),
    ),
  )
}
